import React, { useMemo } from "react";
import { Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";

const getLookupList = (slmLookups, lookupKey) => {
  if (!Array.isArray(slmLookups)) {
    return [];
  }

  const lookupGroup = slmLookups.find((item) => item?.look_up === lookupKey);
  return Array.isArray(lookupGroup?.list) ? lookupGroup.list : [];
};

const getLookupListByCandidates = (slmLookups, lookupKeys) => {
  for (const lookupKey of lookupKeys) {
    const list = getLookupList(slmLookups, lookupKey);
    if (list.length > 0) {
      return list;
    }
  }

  return [];
};

const mapLookupList = (list, candidateValueKeys, candidateLabelKeys) => {
  const firstDefinedValue = (option, keys) => {
    for (const key of keys) {
      if (option && Object.prototype.hasOwnProperty.call(option, key) && option[key] !== undefined && option[key] !== null) {
        return option[key];
      }
    }

    return undefined;
  };

  return list
    .map((option) => {
      if (typeof option === "string") {
        return { value: option, label: option };
      }

      const valueFromCandidates = firstDefinedValue(option, candidateValueKeys);
      const labelFromCandidates = firstDefinedValue(option, candidateLabelKeys);

      const value = valueFromCandidates ?? "";
      const label = labelFromCandidates ?? String(value);

      return {
        ...option,
        value,
        label,
      };
    })
    .filter((option) => option.value !== undefined && option.value !== null);
};

export default function SLMSaleSpecificsPanel({
  slmLookups,
  state,
  titleBasisOptions,
  statusOptions,
  broadSpaceUseOptions,
  detailedSpaceUseOptions,
  saleMethodOptions,
  saleDescriberOptions,
  incomeBasisOptions,
  onTitleBasisChange,
  onStatusChange,
  onBroadSpaceUseChange,
  onSpaceUseChange,
  onSaleMethodChange,
  onSaleDescriberChange,
  onDateChange,
  onChange,
  onBlur,
  onMoneyChange,
  onCheckBoxChange,
  onNumberChange,
  onPercentChange,
  onIncomeBasisChange,
  createId,
}) {
  const derivedTitleBasisOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "sales_title_category"),
      ["value", "title_basis_cat_id", "sales_title_cat_id", "sales_title_category_id", "id", "code", "name", "label"],
      ["label", "title_basis_cat", "sales_title_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : titleBasisOptions || [];
  }, [slmLookups, titleBasisOptions]);

  const derivedStatusOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "sales_status_category"),
      ["value", "status_cat_id", "sale_status_cat_id", "sales_status_category_id", "id", "code", "name", "label"],
      ["label", "sale_status", "sales_status_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : statusOptions || [];
  }, [slmLookups, statusOptions]);

  const derivedBroadSpaceUseOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "broad_space_use"),
      ["value", "broad_space_use_id", "id", "code", "name", "label"],
      ["label", "broad_space_use_code", "broad_space_use", "name", "description", "code", "value"]
    );

    const baseOptions = lookupValues.length > 0 ? lookupValues : broadSpaceUseOptions || [];

    return baseOptions.filter((option) => {
      if (state.broad_space_use_id === null || state.broad_space_use_id === undefined || state.broad_space_use_id === 1) {
        return true;
      }

      return String(option?.value ?? "") === String(state.broad_space_use_id);
    });
  }, [slmLookups, broadSpaceUseOptions, state.broad_space_use_id]);

  const derivedDetailedSpaceUseOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupListByCandidates(slmLookups, ["space_use", "space_use_category", "spaceusecategory"]),
      ["value", "space_use_id", "space_use_category_id", "id", "code", "name", "label"],
      ["label", "space_use_name", "space_use_code", "space_use", "name", "description", "code", "value"]
    );

    const baseOptions = lookupValues.length > 0 ? lookupValues : detailedSpaceUseOptions || [];

    return baseOptions.filter((option) => {
      if (state.broad_space_use_id === null || state.broad_space_use_id === undefined) {
        return true;
      }

      const optionBroadSpaceUseId = option?.supplementary_details?.broad_space_use_id ?? option?.broad_space_use_id;
      return String(optionBroadSpaceUseId ?? "") === String(state.broad_space_use_id);
    });
  }, [slmLookups, detailedSpaceUseOptions, state.broad_space_use_id]);

  const derivedSaleMethodOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "sales_method_category"),
      ["value", "sale_method_cat_id", "sales_method_category_id", "id", "code", "name", "label"],
      ["label", "sale_method", "sales_method_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : saleMethodOptions || [];
  }, [slmLookups, saleMethodOptions]);

  const derivedSaleDescriberOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "sales_describer_category"),
      ["value", "sale_describer_cat_id", "sales_describer_category_id", "id", "code", "name", "label"],
      ["label", "sale_describer", "sales_describer_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : saleDescriberOptions || [];
  }, [slmLookups, saleDescriberOptions]);

  const derivedIncomeBasisOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupListByCandidates(slmLookups, ["sales_incom_basis_category", "sales_income_basis_category"]),
      ["value", "income_basis_cat_id", "sales_income_basis_category_id", "id", "code", "name", "label"],
      ["label", "income_basis", "sales_income_basis_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : incomeBasisOptions || [];
  }, [slmLookups, incomeBasisOptions]);

  return (
    <Card className="mb-2">
      <Card.Header>Sale Specifics</Card.Header>
      <Card.Body>
        <Form.Group as={Row} controlId="title_basis">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Title Basis
          </Form.Label>
          <Col>
            <Select inputId="title-basis" instanceId={createId("field", "title-basis", "instance")} value={state.selectedTitleBasisCategories} onChange={onTitleBasisChange} onBlur={onBlur} options={derivedTitleBasisOptions} isClearable />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="sale_status_cat">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Status
          </Form.Label>
          <Col>
            <Select value={state.selectedStatusCategories} onChange={onStatusChange} options={derivedStatusOptions} inputId={createId("field", "status", "input")} instanceId={createId("field", "status", "instance")} isClearable />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="broad_space_use_name">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Broad Space Use
          </Form.Label>
          <Col>
            <Select
              value={state.selectedBroadSpaceUseCategories}
              onChange={onBroadSpaceUseChange}
              options={derivedBroadSpaceUseOptions}
              inputId={createId("field", "broad-space-use", "input")}
              instanceId={createId("field", "broad-space-use", "instance")}
              isClearable
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="space_use_name">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Detailed Space Use
          </Form.Label>
          <Col>
            <Select
              value={state.selectedSpaceUseCategories}
              onChange={onSpaceUseChange}
              options={derivedDetailedSpaceUseOptions}
              inputId={createId("field", "detailed-space-use", "input")}
              instanceId={createId("field", "detailed-space-use", "instance")}
              isClearable
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="sale_method">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Sale Method
          </Form.Label>
          <Col>
            <Select value={state.selectedSaleMethodCategories} onChange={onSaleMethodChange} options={derivedSaleMethodOptions} inputId={createId("field", "sale-method", "input")} instanceId={createId("field", "sale-method", "instance")} isClearable />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="sale_describer">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Describer
          </Form.Label>
          <Col>
            <Select value={state.selectedSaleDescriberCategories} onChange={onSaleDescriberChange} options={derivedSaleDescriberOptions} inputId={createId("field", "describer", "input")} instanceId={createId("field", "describer", "instance")} isClearable />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="stat_date">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Stats Date
          </Form.Label>
          <Col>
            <DatePicker selected={state.stat_date !== null ? new Date(state.stat_date) : null} onChange={(e) => onDateChange(e, "stat_date")} onBlur={onBlur} dateFormat="dd/MM/yyyy" className="form-control" isClearable={true} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="sale_date">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Sale Date (contract)
          </Form.Label>
          <Col>
            <DatePicker selected={state.sale_date !== null ? new Date(state.sale_date) : null} onChange={(e) => onDateChange(e, "sale_date")} onBlur={onBlur} dateFormat="dd/MM/yyyy" className="form-control" isClearable={true} />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="vg_trans_date">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Transfer Date
          </Form.Label>
          <Col>
            <DatePicker selected={state.vg_trans_date !== null ? new Date(state.vg_trans_date) : null} onChange={(e) => onDateChange(e, "vg_trans_date")} onBlur={onBlur} dateFormat="dd/MM/yyyy" className="form-control" isClearable={true} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="transfer_number">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Transfer Number
          </Form.Label>
          <Col>
            <Form.Control onChange={onChange} value={state.transfer_number} as="input" type="text" onBlur={onBlur} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="sale_price">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Sale Price
          </Form.Label>
          <Col>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control onChange={onMoneyChange} value={state.sale_price} as="input" type="text" onBlur={onBlur} />
            </InputGroup>
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="estimated">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Price Estimated?
          </Form.Label>
          <Col>
            <Form.Check checked={state.estimated} onChange={onCheckBoxChange} name="estimated" type="checkbox" />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="net_income">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Income
          </Form.Label>
          <Col>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control onChange={onNumberChange} value={state.net_income} as="input" type="text" onBlur={onBlur} />
            </InputGroup>
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="income_basis">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Basis
          </Form.Label>
          <Col>
            <Select value={state.selectedIncomeBasisCategories} onChange={onIncomeBasisChange} options={derivedIncomeBasisOptions} inputId={createId("field", "income-basis", "input")} instanceId={createId("field", "income-basis", "instance")} isClearable />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="sale_yield">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Yield
          </Form.Label>
          <Col>
            <InputGroup>
              <InputGroup.Text>%</InputGroup.Text>
              <Form.Control onChange={onNumberChange} value={state.sale_yield ? state.sale_yield : state.sale_yield} as="input" type="text" onBlur={onBlur} />
            </InputGroup>
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="percent_sold">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Interest
          </Form.Label>
          <Col>
            <InputGroup>
              <InputGroup.Text>%</InputGroup.Text>
              <Form.Control onChange={onPercentChange} value={state.percent_sold ? state.percent_sold : state.percent_sold} as="input" type="text" onBlur={onBlur} />
            </InputGroup>
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="mortgagee">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Mortgagee Sale?
          </Form.Label>
          <Col>
            <Form.Check checked={state.mortgagee} onChange={onCheckBoxChange} name="mortgagee" type="checkbox" />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="foreign_investment">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Foreign Investment?
          </Form.Label>
          <Col>
            <Form.Check checked={state.foreign_investment} onChange={onCheckBoxChange} name="foreign_investment" type="checkbox" />
          </Col>
        </Form.Group>

        {false && (
          <Form.Group as={Row} controlId="lot_plan">
            <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
              Lot Plan
            </Form.Label>
            <Col>
              <Form.Control onChange={onChange} value={state.lot_plan} as="input" type="text" onBlur={onBlur} />
            </Col>
          </Form.Group>
        )}

        <Form.Group as={Row} controlId="editors_note">
          <Form.Label>
            <b>Editors Notes</b>
            <br />
            (Unpublished)
          </Form.Label>

          <Form.Control onChange={onChange} value={state.editors_note} as="textarea" type="text" rows="5" onBlur={onBlur} bsPrefix="internal" />
        </Form.Group>
      </Card.Body>
    </Card>
  );
}
